import { TWIN_TYPE } from './config';
import { english } from 'src/i18n/language';

/**
 * The words for a multiple birth — TGCLOUD-1692.
 *
 * ONE module, because before this the product had no vocabulary for a set of three at
 * all. A triplet read "Identical twin of UPN 4, UPN 5"; the sidebar card was headed
 * TWIN STATUS; the clear button said "Not a twin"; and `deleteConsequences` counted
 * "twin links". Four places describing the same set, none of them able to say
 * "triplet". Wording that lives next to the thing it describes drifts, so it lives
 * here instead and the sidebar, the context menu and the delete dialog all read it.
 *
 * Two rules this module exists to hold:
 *
 * 1. **The noun follows the SET SIZE**, never a stored field. `siblingType` is a
 *    denormalisation of size and has been observed carrying `twin` for a set of three
 *    (the context-menu path) and `quadruplet` for a set of five (the old count
 *    mapping). Deriving the word from the members actually present cannot drift.
 *
 * 2. **The zygosity adjective follows the PAIR, not the set.** A set of three or more
 *    does not have one zygosity — `twinRelations[].type` is per pair, and the common
 *    clinical case is one identical pair alongside a third who is not. So anything
 *    above a pair may need to say two different things in one sentence, and the
 *    wording has to be built from segments rather than a single label lookup.
 */

/** Singular noun by set size. Above six there is no ordinary English word we should
 *  put in a clinical record, so `multipleBirthPhrase` falls back to a count. */
export const MULTIPLE_BIRTH_NOUN = Object.freeze({
  2: 'twin',
  3: 'triplet',
  4: 'quadruplet',
  5: 'quintuplet',
  6: 'sextuplet'
});

/** Card header and section title. "Multiple birth" is the umbrella term that stays
 *  correct at every size, which "Twin status" does not. */
export const MULTIPLE_BIRTH_HEADER = 'Multiple birth';

/**
 * The ADJECTIVE form of a zygosity, for sentences that already carry the noun.
 *
 * `TWIN_TYPE_LABELS` (twinLinkage.js) gives the standalone label ("Identical twin"),
 * which cannot be reused above a pair — "Identical twin of A, B" for a triplet is the
 * defect. The sentences below spell both out in full, one key each, so a language can
 * inflect them: a change to either wording is a change to those sentences too.
 *
 * `unknown` has no adjective on purpose: "unknown-zygosity triplets" is worse English
 * than the phrase the builders below use for it, and `singleton` never reaches display
 * because it means "not a multiple" rather than a kind of one.
 */
export const ZYGOSITY_ADJECTIVE = Object.freeze({
  [TWIN_TYPE.monozygotic]: 'identical',
  [TWIN_TYPE.dizygotic]: 'non-identical',
  [TWIN_TYPE.unknown]: null
});

/** Plural noun by set size, written out rather than made by adding an "s", so a
 *  translation is given the whole word. */
const MULTIPLE_BIRTH_PLURAL = Object.freeze({
  2: 'twins',
  3: 'triplets',
  4: 'quadruplets',
  5: 'quintuplets',
  6: 'sextuplets'
});

// Every sentence below is built for the screen: each builder takes the caller's
// `t`, English by default, and each wording is ONE key per set size and zygosity,
// so a language can inflect the noun with its adjective and its number. The
// names in a sentence are values, filled in after the lookup.

/** Singular noun, or null above six. */
export const multipleBirthNoun = (size) => MULTIPLE_BIRTH_NOUN[size] || null;

/** Plural noun, or null above six. */
export const multipleBirthPlural = (size) =>
  MULTIPLE_BIRTH_PLURAL[size] || null;

/**
 * How to refer to the set as a whole: "triplets", or "a multiple birth of 7".
 *
 * The fallback is deliberately a count rather than "septuplets". Sets that large are
 * vanishingly rare and almost always a data-entry error; printing the count invites
 * the reader to check it, where a confident Latin noun invites them to believe it.
 */
export const multipleBirthPhrase = (size, t = english) => {
  const plural = multipleBirthPlural(size);
  return plural
    ? t(plural)
    : t('a multiple birth of {{count}}', { count: size });
};

/** "a", "a and b", "a, b and c" — an Oxford-comma-free list, matching how the rest of
 *  the sidebar reads. */
export const formatNameList = (names, t = english) => {
  const list = (names || []).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return list[0];
  return t('{{items}} and {{last}}', {
    items: list.slice(0, -1).join(', '),
    last: list[list.length - 1]
  });
};

/**
 * One clause describing this person's relation to the partners sharing one zygosity.
 *
 * "identical to UPN 4 - Jane Doe", or "zygosity unknown with UPN 5" — `with` rather
 * than `to` for the unknown case, because "unknown to" would read as not knowing the
 * person.
 */
const SEGMENT = Object.freeze({
  [TWIN_TYPE.monozygotic]: 'identical to {{names}}',
  [TWIN_TYPE.dizygotic]: 'non-identical to {{names}}'
});

const zygositySegment = ({ type, labels }, t) =>
  t(SEGMENT[type] || 'zygosity unknown with {{names}}', {
    names: formatNameList(labels, t)
  });

/** A pair's line, by zygosity: TWIN_TYPE_LABELS' wording, then "of" and the names. */
const PAIR_LINE = Object.freeze({
  [TWIN_TYPE.monozygotic]: 'Identical twin of {{names}}',
  [TWIN_TYPE.dizygotic]: 'Non-identical twin of {{names}}',
  [TWIN_TYPE.unknown]: 'Zygosity unknown of {{names}}'
});

/** A set of three to six of one zygosity, by zygosity and size. */
const ONE_KIND_LINE = Object.freeze({
  [TWIN_TYPE.monozygotic]: {
    3: 'One of identical triplets, with {{names}}',
    4: 'One of identical quadruplets, with {{names}}',
    5: 'One of identical quintuplets, with {{names}}',
    6: 'One of identical sextuplets, with {{names}}'
  },
  [TWIN_TYPE.dizygotic]: {
    3: 'One of non-identical triplets, with {{names}}',
    4: 'One of non-identical quadruplets, with {{names}}',
    5: 'One of non-identical quintuplets, with {{names}}',
    6: 'One of non-identical sextuplets, with {{names}}'
  },
  [TWIN_TYPE.unknown]: {
    3: 'One of triplets of unknown zygosity, with {{names}}',
    4: 'One of quadruplets of unknown zygosity, with {{names}}',
    5: 'One of quintuplets of unknown zygosity, with {{names}}',
    6: 'One of sextuplets of unknown zygosity, with {{names}}'
  }
});

/**
 * The at-rest status line for a member of a multiple birth.
 *
 * @param {number} size   how many people are in the set, INCLUDING this one
 * @param {Array}  groups [{ type, labels }] — this person's confirmed partners,
 *                        grouped by the zygosity of the pair, already display-labelled
 * @param {Function} [t]  the caller's t(), English by default
 * @returns {?string} null when this person is not in a set
 *
 * A PAIR keeps its existing wording exactly ("Identical twin of UPN 4 - Jane Doe").
 * That is not laziness: every twin already recorded reads that way today, and changing
 * it would churn the sidebar for the overwhelmingly common case in order to serve the
 * rare one. Three and above get the new forms, because they currently have no correct
 * form at all.
 */
export const multipleBirthStatusLine = ({ size, groups }, t = english) => {
  const present = (groups || []).filter((g) => g && (g.labels || []).length);
  if (!present.length) return null;

  // A pair: unchanged from TGCLOUD-1643.
  if (size === 2) {
    const { type, labels } = present[0];
    return t(PAIR_LINE[type] || 'Twin of {{names}}', {
      names: formatNameList(labels, t)
    });
  }

  const mixed = present.length > 1;
  const named = multipleBirthPlural(size);

  // Above six, always the segment form — "One of an identical multiple birth of 7"
  // is not a sentence anybody should have to read.
  if (mixed || !named) {
    return t('One of {{set}} — {{segments}}', {
      set: multipleBirthPhrase(size, t),
      segments: present.map((g) => zygositySegment(g, t)).join('; ')
    });
  }

  const { type, labels } = present[0];
  const lines = ONE_KIND_LINE[type] || ONE_KIND_LINE[TWIN_TYPE.unknown];
  return t(lines[size], { names: formatNameList(labels, t) });
};

/**
 * The label on the control that takes this person OUT of the set.
 *
 * Size-aware because the operation genuinely differs. Leaving a pair dissolves it, so
 * "Not a twin" is the whole truth. Leaving a triplet does not dissolve anything — the
 * other two stay twins — so the label names what this person stops being, and
 * `remainderConsequence` supplies the rest.
 */
const NOT_A_MULTIPLE = Object.freeze({
  2: 'Not a twin',
  3: 'Not a triplet',
  4: 'Not a quadruplet',
  5: 'Not a quintuplet',
  6: 'Not a sextuplet'
});

export const notAMultipleLabel = (size, t = english) =>
  t(NOT_A_MULTIPLE[size] || 'Remove from this set');

/**
 * What happens to everyone else, stated before the click rather than discovered after.
 *
 * Returns null for a pair: there is no remainder to describe, and a sentence saying so
 * would be noise. Same reasoning as `deleteConsequences`, which drops any bullet that
 * would read "0".
 *
 * The small numbers are written out: "The other two will remain twins" reads as prose;
 * "The other 2 will remain twins" reads as a form field.
 */
const REMAINDER = Object.freeze({
  2: 'The other two will remain twins.',
  3: 'The other three will remain triplets.',
  4: 'The other four will remain quadruplets.',
  5: 'The other five will remain quintuplets.',
  6: 'The other six will remain sextuplets.'
});

export const remainderConsequence = (size, t = english) => {
  const remaining = Number(size) - 1;
  if (!Number.isFinite(remaining) || remaining < 2) return null;
  return REMAINDER[remaining]
    ? t(REMAINDER[remaining])
    : t('The other {{count}} will remain a multiple birth of {{count}}.', {
        count: remaining
      });
};

/**
 * The delete-dialog consequence of removing a member of a set of THREE OR MORE.
 *
 * `deleteConsequences` counts relations — "remove 2 twin links" — which is an
 * implementation detail rather than a consequence, and never names the set. Above a pair
 * what a clinician needs to know is what SURVIVES: deleting one of three co-multiples
 * leaves two twins, not three singletons.
 *
 * Returns null below three, so the caller keeps its existing "remove 1 twin link". A pair
 * has no remainder to describe and the count is not misleading at that size — the same
 * call made for the status line, and it leaves three existing tests standing.
 */
const DEPARTURE = Object.freeze({
  3: 'remove them from the triplet set, leaving the other two as twins',
  4: 'remove them from the quadruplet set, leaving the other three as triplets',
  5: 'remove them from the quintuplet set, leaving the other four as quadruplets',
  6: 'remove them from the sextuplet set, leaving the other five as quintuplets',
  7: 'remove them from the multiple birth set, leaving the other six as sextuplets'
});

export const setDepartureLine = (size, t = english) => {
  if (!Number.isFinite(Number(size)) || Number(size) < 3) return null;
  const n = Number(size);
  return DEPARTURE[n]
    ? t(DEPARTURE[n])
    : t(
        'remove them from the multiple birth set, leaving the other {{count}} as a multiple birth of {{count}}',
        { count: n - 1 }
      );
};
